/**
 * Analytics Event Types
 *
 * Centralized enum for all tracking events.
 * Use these values when calling track() to ensure consistency.
 */
export enum AnalyticsEvent {
  // Core Actions
  CtaTapped = "CTA Tapped",
  PageViewed = "Page Viewed",

  // Authentication
  OrgSignUpViewed = "Org Sign Up Viewed",
  SignUpViewed = "Sign Up Viewed",
  SignUpStarted = "Sign Up Started",
  SignedUp = "Signed Up",
  AccountCreated = "Account Created",
  // Mirrors "Sign Up Started"; name still to confirm with analytics.
  SignInStarted = "Sign In Started",
  SignedIn = "Signed In",
  SignedOut = "Signed Out",
  EmailEntered = "Email Entered",

  // Forms
  FormSubmitted = "Form Submitted",
  FormSubmittedError = "Form Submitted Error",

  // Subscriptions
  Subscribed = "Subscribed",
  OrderCompleted = "Order Completed",

  // Content Interactions
  ContentSearched = "Content Searched",
  ContentFavorited = "Content Favorited",
  ContentFavoritedRemoved = "Content Favorited Removed",
  ContentStarted = "Content Started",
  ContentPlayButtonSelected = "Content Play Button Selected",
  ContentScreenExpandSelected = "Content Screen Expand Selected",
  ContentFastForwarded = "Content Fast Forwarded",
  ContentStopped = "Content Stopped",
  ContentRewinded = "Content Rewinded",
  ContentPauseButtonSelected = "Content Pause Button Selected",
  ContentCompleted = "Content Completed",
  ContentCasted = "Content Casted",
  ContentCastedCancelled = "Content Casted Cancelled",
  ContentShared = "Content Shared",
  ContentSkipped = "Content Skipped",

  // UI Elements
  ModalShown = "Modal Shown",
  ModalClosed = "Modal Closed",

  // Fitness Assessment
  FitnessAssessmentStarted = "Fitness Assessment Started",
  FitnessAssessmentQuestion = "Fitness Assessment Question",
  FitnessAssessmentContentSelected = "Fitness Assessment Content Selected",
  FitnessAssessmentSucceeded = "Fitness Assessment Succeeded",
  QuizSkip = "Quiz - Skip",

  // Paths
  PathJoined = "Path Joined",
  PathCompleted = "Path Completed",
  OpenPath = "Open Path",
  PathWeekCompleted = "Path Week Completed",
  PathShared = "Path Shared",
  PathFeedbackSent = "Path Feedback Sent",
  PathLeft = "Path Left",
  PathRestarted = "Path Restarted",
  PostPathModalViewed = "Post Path Modal Viewed",
  PostPathShareSelected = "Post Path Share Selected",

  // Routines
  RoutineShareEmailSubmitted = "Routine Share Email Submitted",
  PostRoutineModalViewed = "Post Routine Modal Viewed",
  RoutineRated = "Routine Rated",
  RoutineFeedbackSent = "Routine Feedback Sent",
  PostRoutineShareSelected = "Post Routine Share Selected",

  // Mobility Journey
  MobilityJourneyLetsGetMoving = "Mobility Journey Let's get moving",
  MobilityJourneyStartYourJourney = "Mobility Journey Start your journey",
  MobilityJourneyTailorYourContent = "Mobility Journey Tailor Your Content",
  MobilityJourneyCompleted = "Mobility Journey Completed",
  MobilityJourneyTaskSelected = "Mobility Journey Task Selected",

  // Discovery & Navigation
  PoseDetailSelected = "Pose Detail Selected",
  DiscoverSelected = "Discover Selected",
  HubSelected = "Hub Selected",
  FeaturedSelected = "Featured Selected",
  JourneyOptionSelected = "Journey Option Selected",

  // Programs
  ProgramCreated = "Program Created",
  ProgramWeekCompleted = "Program Week Completed",
  ProgramCompleted = "Program Completed",
  ProgramEnded = "Program Ended",
  ProgramWeekSkipped = "Program Week Skipped",
  ProgramShareSelected = "Program Share Selected",
  ProgramRecapStarted = "Program Recap Started",
  ProgramRecapCompleted = "Program Recap Completed",
  PostProgramShareSelected = "Post-Program Share Selected",
  ProgramRecapCreateNewProgram = "Program Recap - Create New Program",
  ProgramRecapWatchAgain = "Program Recap - Watch Again",
  ProgramCatchUp = "Program Catch Up",

  // Challenges
  ChallengeJoined = "Challenge Joined",
  ChallengeCompleted = "Challenge Completed",
  BadgeShareSelected = "Badge Share Selected",

  // Interests
  InterestsUpdated = "Interests Updated",
  InterestAdded = "Interest Added",
  InterestRemoved = "Interest Removed",

  // Activity
  ActivityShared = "Activity Shared",

  // A/B Testing & Tracking
  ViewedABTest = "Viewed A/B Test",
  KameleoonIdentifier = "Kameleoon identifier",
}

export type AnalyticsEventType = AnalyticsEvent;
